import _ from 'lodash';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import RackTransferContainer from '../../containers/accounting/RackTransfer';
import BulkCustomersContainer from '../../containers/logistics/BulkCustomers';
import CarriersContainer from '../../containers/logistics/Carriers';
import TerminalsContainer from '../../containers/logistics/Terminals';
import DocumentContainer from '../../containers/accounting/Document';

import SaveInvoiceDocumentsDialog from '../../components/InvoiceDocument/SaveDialog';
import ConfirmInvalidateRackTransferDialog from '../../components/Dialogs/ConfirmInvalidateRackTransferDialog';
import AuthUserContainer from '../../containers/AuthUser';

import { statusClassNames, formatStatus } from '../../libs/utility';

import {
	documentTypeSort,
	MIN_GALLONS_FOR_BULK_WARNING,
	MAX_GALLONS_FOR_BULK_WARNING,
	getDocuments,
	INTERNAL_TERMINAL_ID,
	invoiceStatusClassNames,
	statusToDisplay,
	//matchProductToBulkDelivery,
} from './general';

//- UI
import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPRegionHeader from '../../components/ui/FMPRegionHeader';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import documentIcon from '../../images/document.svg';
import attachIcon from '../../images/attachDocument.svg';
import InvoiceDocumentIcon from '../../components/ui/invoiceDocumentIcon';
import InvoiceDocumentPopup from '../../components/Dialogs/InvoiceDocumentPopup';
import CheckIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';

//- Form
import ResourceUploader from '../../components/form/ResourceUploader';
import FMPSubmitButton from '../../components/form/FMPSubmitButton';

//- Pure form
import FMPDatePicker from '../../components/pureForm/FMPDatePicker';
import FMPCarrierSelect from '../../components/pureForm/FMPCarrierSelect';
import GenericSavingDialog from '../../components/Dialogs/GenericSavingDialog';
import FMPTerminalSupplierSelect from '../../components/pureForm/FMPTerminalSupplierSelect';
import FMPTerminalSelect from '../../components/pureForm/FMPTerminalSelect';
import FMPBulkCustomerSelect from '../../components/pureForm/FMPBulkCustomerSelect';

import { valueToDisplay } from '../../libs/products';
import FMPText from '../../components/pureForm/FMPText';

const DEFAULT_TERMINAL_SUPPLIER_VALUE = { terminalId: '', supplierId: '' };

const styles = (theme) => {
	return {
		...statusClassNames,
		paper: {
			width: '100%',
			overflowX: 'auto',
			textAlign: 'left',
			boxSizing: 'border-box',
			padding: 25,
			paddingBottom: 30,
			marginTop: 20,
			[theme.breakpoints.up('md')]: {
				padding: '10px 40px 40px 40px',
			},
		},
		inputGroup: {
			borderLeft: '5px solid #ccc',
			marginTop: 10,
			marginBottom: 20,
		},
		submitButton: {
			fontSize: 18,
			borderRadius: '100px',
			padding: '20px 30px',
			textTransform: 'uppercase',
		},
		addInputButton: {
			borderRadius: '100px',
			marginTop: 20,
		},
		inset: {
			paddingLeft: 5,
			lineHeight: 1.3,
		},
		linkedTo: {
			fontSize: 20,
			paddingTop: 10,
		},
		linkedStatus: {
			fontSize: 15,
		},
		documentContainer: {
			display: 'flex',
			justifyContent: 'space-between',
			borderBottom: '1px solid #F1F1F1',
			paddingBottom: 7,
			marginBottom: 7,
			marginLeft: 10,
			marginTop: 10,
		},
		documentInfoContainer: {
			display: 'flex',
			justifyContent: 'flex-start',
		},
		documentInfo: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
		},
		documentIcons: {
			whiteSpace: 'nowrap',
		},
		documentName: {
			fontWeight: 900,
		},
		button: {},
		missingDocumentName: {
			fontWeight: 900,
			color: '#999',
		},
		documentDetails: {
			fontWeight: 200,
		},
		documentRequired: {
			fontSize: 14,
			color: '#777',
		},
		missingRequiredDocs: {
			padding: 20,
			fontSize: 20,
			margin: '20px 10px',
			border: '1px solid #ddd',
			backgroundColor: '#f5f5f5',
		},
		documentIcon: {
			marginRight: 12,
			display: 'inline-block',
			width: 34,
			height: 40,
			backgroundImage: `url('${documentIcon}')`,
			backgroundPosition: 'center',
			backgroundSize: 'cover',
			verticalAlign: 'middle',
			position: 'relative',
		},
		attachIcon: {
			display: 'inline-block',
			width: 21,
			height: 21,
			backgroundImage: `url('${attachIcon}')`,
			backgroundPosition: 'center',
			backgroundSize: 'cover',
			verticalAlign: 'middle',
			position: 'absolute',
			bottom: 0,
			right: 0,
			transform: 'translate(30%, 30%)',
		},
		matchContainer: {
			backgroundColor: '#f7f7f7',
			padding: 15,
			fontSize: 15,
			fontWeight: 400,
			marginBottom: 10,
			marginTop: 5,
		},
		matchRow: {
			display: 'flex',
			marginBottom: 5,
		},
		warningIcon: {
			color: '#FFC107',
			width: 16,
			height: 16,
			paddingRight: 5,
			borderRight: '1px solid #f1f1f1',
			verticalAlign: 'middle',
		},
		checkIcon: {
			color: '#4CAF50',
			width: 16,
			height: 16,
			paddingRight: 5,
			verticalAlign: 'middle',
		},
		topErrors: {
			fontSize: 18,
			padding: 20,
			border: '2px solid #f44336',
			margin: 20,
			backgroundColor: 'white',
		},
		infoBox: {
			fontSize: 14,
			alignItems: 'center',
			display: 'flex',
			backgroundColor: '#f7f7f7',
			padding: '10px 15px 15px 15px',
			marginTop: 10,
			marginBottom: 5,
		},
		infoIcon: {
			color: '#2196F3',
			width: 32,
			height: 32,
			paddingRight: 5,
			verticalAlign: 'middle',
		},
	};
};

class SaveRackTransfer extends Component {
	constructor(props) {
		super(props);
		const rackTransferId = _.get(this.props, ['match', 'params', 'rackTransferId']);

		this.state = {
			rackTransferId: rackTransferId ? parseInt(rackTransferId, 10) : undefined,
			isSaveDocumentDialogOpen: false,
			isConfirmInvalidateOpen: false,
			isUpdate: rackTransferId,
			formFieldError: {},
			invoiceDocuments: [],
			isInvoiceDocumentPopupOpen: false,
			invoiceDocumentPoppedUp: undefined,
			isSavingOpen: false,
			savingStatus: undefined,
		};
	}

	componentDidMount() {
		const {
			rackTransfer,
			getRackTransfer,
			carriers,
			searchCarriers,
			terminals,
			searchTerminals,
			bulkCustomers,
			searchBulkCustomers,
		} = this.props;

		const { isUpdate, rackTransferId } = this.state;

		if (_.isEmpty(carriers)) {
			searchCarriers();
		}
		if (_.isEmpty(terminals)) {
			searchTerminals();
		}
		if (_.isEmpty(bulkCustomers)) {
			searchBulkCustomers();
		}

		//- Its always an update
		if (!isUpdate) {
			return;
		}

		if (!rackTransfer) {
			getRackTransfer({
				params: {
					rackTransferIds: [rackTransferId],
				},
			});
		} else {
			const newState = this.getStateFromRackTransfer(this.props);
			this.setState(newState);
		}
	}

	componentDidUpdate(prevProps) {
		if (_.isUndefined(prevProps.rackTransfer) && !_.isUndefined(this.props.rackTransfer)) {
			const newState = this.getStateFromRackTransfer(this.props);
			this.setState(newState);
		}
	}

	getStateFromRackTransfer = (props) => {
		const { rackTransfer, invoiceDocumentMap, bulkCustomerMap } = props;

		if (!rackTransfer) {
			return;
		}

		const newState = {};
		[
			'bulkCustomerId',
			'deliveryDate',
			'carrierId',
			'carrierPoNumber',
			'invoiceDocumentIds',

			'offloadTerminalId',
			'offloadBolNumbers',
			'offloadNetGallons',

			'productType',
			'pickupBolNumbers',
			'pickupNetGallons',
			'loadingNumber',
		].forEach((key) => {
			if (_.isNil(rackTransfer[key])) {
				return;
			}
			newState[key] = rackTransfer[key];
		});

		const { bulkCustomerId, invoiceDocumentIds, pickupTerminalId, pickupSupplierId } = rackTransfer;

		if (pickupTerminalId && pickupSupplierId) {
			newState.pickupTerminalSupplier = {
				terminalId: pickupTerminalId,
				supplierId: pickupSupplierId,
			};
		}

		newState.invoiceDocuments = [];
		invoiceDocumentIds.forEach((invoiceDocumentId) => {
			const invoiceDocument = invoiceDocumentMap[invoiceDocumentId] || {
				invoiceDocumentId,
				documentType: 'Missing',
			};
			newState.invoiceDocuments.push(invoiceDocument);
		});

		const errors = [];
		const bulkCustomer = bulkCustomerMap[bulkCustomerId];
		if (!bulkCustomer) {
			return this.setState({ errors: [`Major error : Missing bulk customer ${bulkCustomerId}`] });
		}

		if (!_.isEmpty(errors)) {
			newState.errors = errors;
		}

		return newState;
	};

	//- Complicated because it handles values as events or paths as arrays
	onChange = (value, path) => {
		const arrayPath = _.isString(path) ? _.toPath(path) : path;
		const val = _.isObject(value) && value.target ? value.target.value : value;

		const basePath = _.head(arrayPath);
		let base = this.state[basePath];
		let newState = {};

		if (_.isArray(base) || _.isObject(base)) {
			if (arrayPath.length > 1) {
				base = _.cloneDeep(base);
				_.set(base, arrayPath.slice(1), val);
				newState[basePath] = base;
			} else {
				newState[basePath] = val;
			}
		} else {
			newState[basePath] = val;
		}

		this.setState(newState);
	};

	onTerminalSupplierChange = (value, path) => {
		const { terminalId, supplierId } = value;
		let newVal = { terminalId, supplierId };
		newVal = _.pickBy(newVal, (v) => {
			return !_.isUndefined(v);
		});

		if (_.keys(newVal).length) {
			const existing = _.get(this.state, path, {});
			newVal = _.assign({}, existing, newVal);
			this.onChange(newVal, path);
		}
	};

	//- Dialog and saving
	closeSaveDocumentDialog = () => {
		this.setState({ isSaveDocumentDialogOpen: false });
	};

	closeSavingDialog = () => {
		this.setState({ onContinue: false });
	};

	continueSavingDialog = () => {
		this.props.history.push(`/accounting/rackTransfers`);
	};

	handleInvalidate = () => {
		this.setState({ isConfirmInvalidateOpen: true });
	};

	closeInvalidateDialog = () => {
		this.setState({ isConfirmInvalidateOpen: false });
	};

	//- New documents or updates to existing ones
	handleNewInvoiceDocuments = (r) => {
		const newInvoiceDocuments = r.invoiceDocuments;
		const existingInvoiceDocuments = this.state.invoiceDocuments;

		const newState = {};
		let invoiceDocuments = newInvoiceDocuments;
		existingInvoiceDocuments.forEach((existing) => {
			if (!_.find(invoiceDocuments, { invoiceDocumentId: existing.invoiceDocumentId })) {
				invoiceDocuments.push(existing);
			}
		});
		newState.invoiceDocuments = invoiceDocuments;

		//- Go through all documents and get the which products
		const { rackTransfer, bulkDeliveryMap } = this.props;
		const { linkedBulkDeliveryId } = rackTransfer;
		const bulkDelivery = bulkDeliveryMap[linkedBulkDeliveryId];

		if (!bulkDelivery) {
			this.setState({ errors: 'Missing linked bulk delivery' });
			return;
		}

		//- Special Case : If this is the INTERNAL terminal ID we don't require the delivery ticket
		//- so we default everything based on the bulk delivery
		const { pickupTerminalId, deliveryDate, carrierId } = bulkDelivery;
		if (pickupTerminalId === INTERNAL_TERMINAL_ID) {
			newState.carrierId = carrierId;
			newState.carrierPoNumber = deliveryDate;
			newState.deliveryDate = deliveryDate;
		}

		// offload doc is optional so we prefill the data

		const { deliveryTerminalId } = bulkDelivery;
		let hasOffloadDoc = false;

		invoiceDocuments.forEach((invoiceDocument) => {
			const { documentType, data } = invoiceDocument;

			//- Carrier
			if (documentType === 'carrierDeliveryTicket') {
				const { carrierId, carrierPoNumber, deliveryDate } = data;
				newState.carrierId = carrierId;
				newState.carrierPoNumber = carrierPoNumber;
				newState.deliveryDate = deliveryDate;
			} else if (documentType === 'pickupBol') {
				const { terminalSupplier, bolNumber, netGallons, loadingNumber } = data;
				newState.pickupTerminalSupplier = terminalSupplier;

				//- Could be multiple docs so keep adding
				if (!newState.pickupBolNumbers) {
					newState.pickupBolNumbers = [];
				}
				newState.pickupBolNumbers.push(bolNumber);

				if (!newState.pickupNetGallons) {
					newState.pickupNetGallons = 0;
				}
				newState.pickupNetGallons += parseInt(netGallons, 10);

				newState.loadingNumber = loadingNumber;
			} else if (documentType === 'offloadBol') {
				hasOffloadDoc = true;
				const { terminalId, bolNumber, netGallons } = data;
				newState.offloadTerminalId = terminalId;

				//- Could be multiple docs so keep adding
				if (!newState.offloadBolNumbers) {
					newState.offloadBolNumbers = [];
				}
				newState.offloadBolNumbers.push(bolNumber);

				if (!newState.offloadNetGallons) {
					newState.offloadNetGallons = 0;
				}
				newState.offloadNetGallons += parseInt(netGallons, 10);
			}
		});

		if (!hasOffloadDoc) {
			newState.offloadTerminalId = deliveryTerminalId;
			newState.offloadNetGallons = 8000;
		}

		this.setState(newState);
	};

	handleDataFileUpload = ({ resource }) => {
		const { convertToPages, showErrors } = this.props;
		this.setState({ isConvertingToPages: true });
		convertToPages({
			params: {
				documentResourceId: resource.resourceId,
			},
			callback: (err, convertToPagesResult) => {
				const newState = {
					isConvertingToPages: false,
				};

				if (err) {
					this.setState(newState);
					return showErrors([err]);
				}

				newState.convertToPagesResult = convertToPagesResult;
				newState.isSaveDocumentDialogOpen = true;
				newState.invoiceDocumentsToModify = null;
				this.setState(newState);
			},
		});
	};

	handleRemoveInvoiceDocument = ({ invoiceDocumentId }) => {
		const { invoiceDocumentIds, invoiceDocuments } = this.state;

		const newInvoiceDocumentIds = _.filter(invoiceDocumentIds, (id) => {
			return id !== invoiceDocumentId;
		});

		const newInvoiceDocuments = _.filter(invoiceDocuments, (invoiceDocument) => {
			return invoiceDocument.invoiceDocumentId !== invoiceDocumentId;
		});

		this.setState({
			invoiceDocumentIds: newInvoiceDocumentIds,
			invoiceDocuments: newInvoiceDocuments,
		});
	};

	handleEditInvoiceDocument = ({ invoiceDocument }) => {
		const newState = {};
		newState.isSaveDocumentDialogOpen = true;
		newState.convertToPagesResult = null;
		newState.invoiceDocumentsToModify = [invoiceDocument];
		this.setState(newState);
	};

	onDocumentClick = (invoiceDocument) => {
		this.setState({
			isInvoiceDocumentPopupOpen: true,
			invoiceDocumentPoppedUp: invoiceDocument,
		});
	};

	handleCloseInvoiceDocumentPopupOpen = () => {
		this.setState({
			isInvoiceDocumentPopupOpen: false,
			invoiceDocumentPoppedUp: undefined,
		});
	};

	//
	// - Saving
	//
	handleSave = () => {
		const { showErrors } = this.props;

		this.save({
			callback: (err, r) => {
				if (err) {
					this.setState({ isSavingOpen: false });
					return showErrors(_.isArray(err) ? err : [err]);
				}

				const rackTransfer = _.get(r, ['collections', 'rackTransferCollection', 0]);
				if (!rackTransfer) {
					this.setState({ isSavingOpen: false });
					return showErrors(['Save did not return a rack transfer']);
				}

				this.setState({
					rackTransferForSending: rackTransfer,
					savingStatus: 'done',
				});
			},
		});
	};

	handleSaveAndMarkComplete = () => {
		const { showErrors } = this.props;
		this.save({
			markComplete: true,
			callback: (err, r) => {
				if (err) {
					this.setState({ isSavingOpen: false });
					return showErrors(_.isArray(err) ? err : [err]);
				}

				const rackTransfer = _.get(r, ['collections', 'rackTransferCollection', 0]);
				if (!rackTransfer) {
					this.setState({ isSavingOpen: false });
					return showErrors(['Save did not return a rack transfer']);
				}

				this.setState({
					rackTransferForSending: rackTransfer,
					savingStatus: 'done',
				});
			},
		});
	};

	save = ({ markComplete, callback }) => {
		const { updateRackTransfer, bulkCustomerMap } = this.props;

		//- Required fields
		const formFieldError = {};
		const errors = [];

		const _setError = (path, error) => {
			_.set(formFieldError, path, error);
			errors.push(error);
		};

		const bulkCustomerId = this.state.bulkCustomerId;
		if (!bulkCustomerId) {
			_setError(['bulkCustomerId'], `Bulk Customer is required`);
			this.setState({ formFieldError });
			return callback(errors);
		}

		const bulkCustomer = bulkCustomerMap[bulkCustomerId];
		if (!bulkCustomer) {
			return callback([`Bulk Customer ${bulkCustomerId} not found`]);
		}

		let fields = [
			{ fieldName: 'bulkCustomerId', display: 'Bulk Customer' },
			{ fieldName: 'deliveryDate', display: 'Delivery Date' },

			{ fieldName: 'carrierId', display: 'Carrier' },
			{ fieldName: 'carrierPoNumber', display: 'Delivery Ticket Number' },
			{ fieldName: 'offloadNetGallons', display: 'Offload Net Gallons' },
			{ fieldName: 'pickupNetGallons', display: 'Pickup Net Gallons' },
			{ fieldName: 'pickupTerminalSupplier', display: 'Pickup Terminal / Supplier' },
		];

		fields.forEach((s) => {
			if (!this.state[s.fieldName]) {
				_setError([s.fieldName], `${s.display} is required`);
			}
		});

		if (!_.isEmpty(errors)) {
			this.setState({ formFieldError });
			return callback(errors);
		}

		//- Get all the values and do individual checks
		const {
			rackTransferId,
			deliveryDate,
			carrierId,
			carrierPoNumber,
			invoiceDocuments,
			offloadTerminalId,
			offloadBolNumbers,

			productType,
			pickupBolNumbers,
			pickupTerminalSupplier,
			loadingNumber,
		} = this.state;

		const pickupTerminalId = pickupTerminalSupplier.terminalId;
		const pickupSupplierId = pickupTerminalSupplier.supplierId;

		if (!pickupTerminalId || !pickupSupplierId) {
			_setError(['pickupTerminalSupplier'], `Pickup terminal / supplier is required`);
		}

		const pickupNetGallons = parseInt(this.state.pickupNetGallons, 10);
		const offloadNetGallons = parseInt(this.state.offloadNetGallons, 10);

		if (pickupNetGallons >= 10000) {
			_setError('pickupNetGallons', `Pickup net gallons above 10,000. Please recheck your values`);
		}

		if (offloadNetGallons >= 10000) {
			_setError(
				'offloadNetGallons',
				`Offload net gallons above 10,000. Please recheck your values`
			);
		}

		offloadBolNumbers.forEach((bolNumber, bIndex) => {
			if (!bolNumber) {
				_setError(['offloadBolNumbers', bIndex], 'BOL Number required');
			}
		});

		pickupBolNumbers.forEach((bolNumber, bIndex) => {
			if (!bolNumber) {
				_setError(['pickupBolNumbers', bIndex], 'BOL Number required');
			}
		});

		if (!_.isEmpty(errors)) {
			this.setState({ formFieldError });
			return callback(errors);
		}

		const sortedInvoiceDocuments = _.orderBy(
			invoiceDocuments,
			({ documentType, name }) => {
				if (!documentType) {
					return 99;
				}
				return (documentTypeSort[documentType] || 98) + '_' + name;
			},
			'asc'
		);
		const invoiceDocumentIds = _.compact(_.map(sortedInvoiceDocuments, 'invoiceDocumentId'));

		//- Lock and load
		const params = {
			rackTransferId,
			bulkCustomerId,
			deliveryDate,
			carrierId,
			carrierPoNumber,
			invoiceDocumentIds,

			productType,
			pickupTerminalId,
			pickupSupplierId,
			pickupBolNumbers,
			pickupNetGallons,
			loadingNumber,

			offloadTerminalId,
			offloadBolNumbers,
			offloadNetGallons,
		};

		if (markComplete) {
			params.status = 'complete';
		}

		this.setState({
			isSavingOpen: true,
			savingStatus: 'saving',
		});

		updateRackTransfer({
			params,
			callback,
		});
	};

	getInputProps = (path) => {
		return {
			path,
			value: this.getValue(path),
			error: this.getError(path),
			onChange: this.onChange,
			md: 6,
		};
	};

	getValue = (path, defaultValue) => {
		const _d = _.isUndefined(defaultValue) ? '' : defaultValue;
		return _.get(this.state, path, _d);
	};
	getError = (path) => {
		return _.get(this.state.formFieldError, path);
	};

	render() {
		const {
			classes,
			rackTransfer,
			carriers,
			bulkCustomers,
			terminals,
			showErrors,
			invalidateRackTransfer,
			supplierMap,
			bulkDeliveryMap,
			resourceMap,
		} = this.props;

		const {
			isUpdate,
			rackTransferId,
			isSaveDocumentDialogOpen,
			convertToPagesResult,
			invoiceDocumentsToModify,
			//formFieldError,

			//- Put here from initial load and from uploading new ones
			isSavingOpen,
			savingStatus,
			isConfirmInvalidateOpen,
			invoiceDocuments,
			errors,
		} = this.state;

		if (_.isEmpty(terminals) || _.isEmpty(carriers) || _.isEmpty(bulkCustomers) || !rackTransfer) {
			return <div>Fetching information...</div>;
		}

		//- Figure out some shit and save it
		const linkedBulkDeliveryId = rackTransfer.linkedBulkDeliveryId;
		const bulkDelivery = bulkDeliveryMap[linkedBulkDeliveryId];

		if (!bulkDelivery) {
			return <div>Missing the linked bulk delivery {linkedBulkDeliveryId}</div>;
		}

		const { uploadedInvoiceDocuments, missingInvoiceDocuments } = getDocuments({
			invoiceDocuments, //- Ones we have
			bulkDelivery, //- Helps us determine ones we need
			carrierMap: _.keyBy(carriers, 'carrierId'),
			terminalMap: _.keyBy(terminals, 'terminalId'),
			supplierMap,
			isRackTransfer: true,
		});

		const isMissingRequiredDocs = !_.isEmpty(
			_.filter(missingInvoiceDocuments, { isRequired: true })
		);
		const headline = `Update Rack Transfer #${rackTransferId}`;

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					{!_.isEmpty(errors) && (
						<div className={classes.topErrors}>
							{errors.map((error) => {
								return error;
							})}
						</div>
					)}

					<FMPHeadline label={headline} />

					{this.renderBulkDelivery()}

					{this.renderInvoiceDocuments({
						uploadedInvoiceDocuments,
						missingInvoiceDocuments,
					})}

					<Paper className={classes.paper}>
						<FMPRegionHeader isTop={true} label="Rack Transfer Info" />

						{!!rackTransfer && this.displayStatus({ rackTransfer })}

						{isMissingRequiredDocs && (
							<div className={classes.missingRequiredDocs}>
								Please first add all required delivery documents. Then the invoice information will
								be available.
							</div>
						)}

						{!isMissingRequiredDocs && this.renderInvoiceFields()}

						{this.renderButtons({
							isMissingRequiredDocs,
							rackTransfer,
							isUpdate,
						})}
					</Paper>

					<GenericSavingDialog
						open={isSavingOpen}
						onClose={this.closeSavingDialog}
						onContinue={this.continueSavingDialog}
						processingText={'Processing and saving rack transfer.'}
						successText={'Rack transfer saved successfully.'}
						status={savingStatus}
					/>

					<ConfirmInvalidateRackTransferDialog
						open={isConfirmInvalidateOpen}
						onClose={this.closeInvalidateDialog}
						rackTransfer={rackTransfer}
						invalidateRackTransfer={invalidateRackTransfer}
						showErrors={showErrors}
						history={this.props.history}
					/>

					<SaveInvoiceDocumentsDialog
						open={isSaveDocumentDialogOpen}
						onClose={this.closeSaveDocumentDialog}
						convertToPagesResult={convertToPagesResult}
						invoiceDocuments={invoiceDocumentsToModify}
						bulkDelivery={bulkDelivery}
						showErrors={showErrors}
						terminals={terminals}
						carriers={carriers}
						supplierMap={supplierMap}
						bulkDeliveryMap={bulkDeliveryMap}
						rackTransfer={rackTransfer}
						onNewInvoiceDocuments={this.handleNewInvoiceDocuments}
					/>

					<InvoiceDocumentPopup
						open={this.state.isInvoiceDocumentPopupOpen}
						onClose={this.handleCloseInvoiceDocumentPopupOpen}
						invoiceDocument={this.state.invoiceDocumentPoppedUp}
						resourceMap={resourceMap}
					/>
				</Grid>
			</Grid>
		);
	}

	displayStatus = ({ rackTransfer }) => {
		const { status, invalidReason } = rackTransfer;

		return (
			<div>
				<div>
					<span>Rack Transfer Status : </span>
					<span style={invoiceStatusClassNames[status]}>{statusToDisplay[status]}</span>
				</div>
				{status === 'invalid' && !!invalidReason && (
					<div>
						<span>Invalid Reason: {invalidReason}</span>
					</div>
				)}
			</div>
		);
	};

	renderBulkDelivery = () => {
		const {
			bulkDeliveryMap,
			classes,
			carriers,
			terminals,
			supplierMap,
			rackTransfer,
			bulkCustomerMap,
		} = this.props;
		const { linkedBulkDeliveryId } = rackTransfer;
		const bulkDelivery = bulkDeliveryMap[linkedBulkDeliveryId];

		if (!bulkDelivery) {
			return <div>Missing the linked bulk delivery : #{linkedBulkDeliveryId}</div>;
		}

		const {
			deliveryDate,
			carrierId,
			status,
			pickupTerminalId,
			pickupSupplierId,
			deliveryTerminalId,
			deliveryBulkCustomerId,
			productType,
			loadingNumber,
		} = bulkDelivery;

		const deliveryDateFormatted = moment(deliveryDate).format('dddd, MMMM Do, YYYY');
		const carrier = _.find(carriers, { carrierId }) || { name: `Missing Carrier: ${carrierId}` };

		const pickupTerminal = _.find(terminals, { terminalId: pickupTerminalId }) || {
			name: `Missing pickup terminal :${pickupTerminalId}`,
		};

		const pickupSupplier = supplierMap[pickupSupplierId] || {
			name: `Missing pickup supplier:${pickupSupplierId}`,
		};

		const deliveryTerminal = _.find(terminals, { terminalId: deliveryTerminalId }) || {
			name: `Missing offload terminal :${deliveryTerminalId}`,
		};

		const bulkCustomer = bulkCustomerMap[deliveryBulkCustomerId] || {
			name: `Missing bulk customer :${deliveryBulkCustomerId}`,
		};

		const { statusClassName } = formatStatus(status);

		const productTypeDisplay = valueToDisplay(productType);

		return (
			<Accordion className={classes.paper} style={{ marginTop: 0, paddingBottom: 10 }}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ padding: 0 }}>
					<FMPRegionHeader
						isSmaller={true}
						isTop={true}
						label={`Linked Bulk Delivery : #${linkedBulkDeliveryId}`}
					/>
				</AccordionSummary>
				<AccordionDetails style={{ display: 'block' }}>
					<div className={classes.linkedTo}>
						<div>
							Linked to <strong>bulk delivery #{linkedBulkDeliveryId}</strong>
						</div>
						<div className={classes.linkedStatus}>
							Status : <span className={classes[statusClassName]}>{status}</span>
						</div>
					</div>

					<FMPSectionHeader label={'Carrier'} />
					<div className={classes.inset}>
						<div>{carrier.name}</div>
					</div>

					<FMPSectionHeader label={'Delivery Date'} />
					<div className={classes.inset}>
						<div>{deliveryDateFormatted}</div>
					</div>

					<FMPSectionHeader label={'Loading Number'} />
					<div className={classes.inset}>
						<div>{loadingNumber || 'None'}</div>
					</div>

					<FMPSectionHeader label={'Fuel Pickup'} />
					<div className={classes.inset}>
						<div>Fuel : {productTypeDisplay}</div>
						<div>Terminal : {pickupTerminal.name}</div>
						<div>Supplier : {pickupSupplier.name}</div>
					</div>

					<FMPSectionHeader label={'Fuel Offload'} />
					<div className={classes.inset}>
						<div>Customer : {bulkCustomer.name}</div>
						<div>Terminal : {deliveryTerminal.name}</div>
					</div>
				</AccordionDetails>
			</Accordion>
		);
	};

	renderInvoiceDocuments = ({ uploadedInvoiceDocuments, missingInvoiceDocuments }) => {
		const { isDwp, classes, resourceMap } = this.props;

		const { isConvertingToPages } = this.state;

		const c = classes;
		return (
			<Paper className={classes.paper}>
				<FMPRegionHeader isTop={true} label="Delivery Documents" />

				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<ResourceUploader
							name={'dataFileResourceId'}
							label={''}
							afterUpload={this.handleDataFileUpload}
							onlyOne={true}
							customProgress={isConvertingToPages ? 'Converting...' : undefined}
						/>
					</Grid>
					<Grid item xs={12} md={8}>
						<div style={{ paddingTop: 10 }}>
							{uploadedInvoiceDocuments.map((invoiceDocument, i) => {
								const { invoiceDocumentId, name, details, documentType } = invoiceDocument;

								return (
									<div className={classes.documentContainer} key={i}>
										<div className={classes.documentInfoContainer}>
											<InvoiceDocumentIcon
												invoiceDocument={invoiceDocument}
												resourceMap={resourceMap}
												onClick={this.onDocumentClick}
											/>

											<div className={c.documentInfo}>
												<div className={c.documentName}>{name || documentType}</div>
												<div className={c.documentDetails}>{details}</div>
											</div>
										</div>
										<div className={classes.documentIcons}>
											<IconButton
												aria-label="Edit"
												className={classes.button}
												onClick={() => {
													this.handleEditInvoiceDocument({ invoiceDocument });
												}}
											>
												<EditIcon />
											</IconButton>

											<IconButton
												aria-label="Delete"
												className={classes.button}
												onClick={() => {
													this.handleRemoveInvoiceDocument({ invoiceDocumentId });
												}}
											>
												<DeleteIcon />
											</IconButton>
										</div>
									</div>
								);
							})}

							{missingInvoiceDocuments.map((missingInvoiceDocument, j) => {
								const { isRequired, name } = missingInvoiceDocument;
								return (
									<div className={classes.documentContainer} key={j}>
										<div className={classes.documentInfoContainer}>
											<InvoiceDocumentIcon
												invoiceDocument={missingInvoiceDocument}
												isMissing={true}
											/>
											<div className={c.documentInfo}>
												<div className={c.missingDocumentName}>{name}</div>
												{isRequired && <div className={c.documentRequired}>*required</div>}
												{!isRequired && <div className={c.documentRequired}>*optional</div>}
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</Grid>
				</Grid>

				{isDwp && (
					<Grid container spacing={3}>
						<Grid item xs={6}>
							<Button
								onClick={() => {
									this.handleDataFileUpload({ resource: { resourceId: 888888888 } });
								}}
							>
								Test Document (only DWP sees this)
							</Button>
						</Grid>
					</Grid>
				)}
			</Paper>
		);
	};

	renderButtons = ({ isMissingRequiredDocs, rackTransfer, isUpdate }) => {
		const status = _.get(rackTransfer, ['status'], 'new');

		if (status === 'complete' && isUpdate) {
			return (
				<div style={{ textAlign: 'right', margin: '20px 10px' }}>
					<Button onClick={this.handleInvalidate} variant="outlined" style={{ margin: '0px 10px' }}>
						Mark Invalid
					</Button>

					<Button
						onClick={this.handleSave}
						variant="outlined"
						color="primary"
						style={{ margin: '0px 10px' }}
					>
						Save Rack Transfer
					</Button>
				</div>
			);
		}

		return (
			<div style={{ textAlign: 'right', margin: '20px 10px' }}>
				<Button onClick={this.handleClose} variant="outlined" style={{ margin: '0px 10px' }}>
					Cancel
				</Button>

				<Button onClick={this.handleInvalidate} variant="outlined" style={{ margin: '0px 10px' }}>
					Mark Invalid
				</Button>

				<Button
					onClick={this.handleSave}
					variant="outlined"
					color="primary"
					style={{ margin: '0px 10px' }}
				>
					Save Rack Transfer
				</Button>

				<FMPSubmitButton
					onClick={this.handleSaveAndMarkComplete}
					buttonText={`Save & Mark Complete`}
					isDisabled={isMissingRequiredDocs}
					disabledText={'Missing required delivery documents'}
				/>
			</div>
		);
	};

	renderInvoiceFields = () => {
		const { bulkCustomers } = this.props;

		return (
			<Fragment>
				<Grid container spacing={2}>
					<Grid item md={6} xs={12}>
						<FMPSectionHeader label={'Customer'} />
						<FMPBulkCustomerSelect
							path={'bulkCustomerId'}
							onChange={this.onChange}
							value={this.getValue('bulkCustomerId')}
							error={this.getError('bulkCustomerId')}
							bulkCustomers={bulkCustomers}
							readOnly={true}
							helpText={'Rack Transfer is linked to bulk delivery. Customer can not be changed'}
						/>
						{this.renderCarrierSection()}
						{this.renderPickupSection()}
						{this.renderOffloadSection()}
					</Grid>
				</Grid>
			</Fragment>
		);
	};

	renderMatch = (line) => {
		const { classes } = this.props;
		return (
			<div className={classes.matchRow}>
				<CheckIcon className={classes.checkIcon} />
				<span>{line}</span>
			</div>
		);
	};

	renderNotSure = (line) => {
		const { classes } = this.props;
		return (
			<div className={classes.matchRow}>
				<span>{line}</span>
			</div>
		);
	};

	renderMismatch = (lines) => {
		const { classes } = this.props;
		return (
			<div className={classes.matchRow}>
				<WarningIcon className={classes.warningIcon} />
				<div>
					{lines.map((line, i) => {
						return <div key={i}>- {line}</div>;
					})}
				</div>
			</div>
		);
	};

	renderCarrierSection = () => {
		const { classes, rackTransfer, bulkDeliveryMap, carriers } = this.props;
		const { invoiceDocuments, deliveryDate, carrierId } = this.state;

		const { linkedBulkDeliveryId } = rackTransfer;
		const bulkDelivery = bulkDeliveryMap[linkedBulkDeliveryId];

		//- Check if carrier matches delivery and the documents
		const deliveryCarrierId = bulkDelivery.carrierId;
		const deliveryCarrier = _.find(carriers, { carrierId: deliveryCarrierId }) || {
			name: `MISSING CARRIER: ${deliveryCarrierId}`,
		};

		let deliveryMatchSnippet =
			deliveryCarrierId === carrierId
				? this.renderMatch(`Carrier matches delivery`)
				: this.renderMismatch([`Carrier does not match delivery : ${deliveryCarrier.name}`]);

		//- Check if carrier matches document
		let documentCarrierId = 0;
		invoiceDocuments.forEach((invoiceDocument) => {
			const { documentType, data } = invoiceDocument;
			if (!data || documentType !== 'carrierDeliveryTicket') {
				return;
			}
			documentCarrierId = data.carrierId;
		});
		const documentCarrier = _.find(carriers, { carrierId: documentCarrierId }) || {
			name: `MISSING CARRIER: ${documentCarrierId}`,
		};
		let documentMatchSnippet;
		if (documentCarrierId === carrierId) {
			documentMatchSnippet = this.renderMatch(`Carrier matches delivery documents`);
		} else {
			documentMatchSnippet = this.renderMismatch([
				`Carrier does not match delivery documents : ${documentCarrier.name}`,
			]);
		}

		if (bulkDelivery.pickupTerminalId === INTERNAL_TERMINAL_ID) {
			documentMatchSnippet = this.renderMatch(
				'INTERNAL pickup does not require a carrier delivery document'
			);
		}

		//- See if the deliveryDate matches documents and delivery
		let deliveryDateMatchSnippet;

		if (bulkDelivery.deliveryDate === deliveryDate) {
			deliveryDateMatchSnippet = this.renderMatch(`Delivery date matches delivery.`);
		} else {
			deliveryDateMatchSnippet = this.renderMismatch([
				`Delivery date does not match delivery : ${bulkDelivery.deliveryDate}`,
			]);
		}

		return (
			<Fragment>
				<FMPSectionHeader label={'Carrier and Freight'} />

				<FMPCarrierSelect
					path={'carrierId'}
					onChange={this.onChange}
					value={this.getValue('carrierId')}
					error={this.getError('carrierId')}
					carriers={carriers}
				/>
				<div className={classes.matchContainer}>
					{deliveryMatchSnippet}
					{documentMatchSnippet}
				</div>

				<FMPText label={'Carrier PO Number'} {...this.getInputProps('carrierPoNumber')} />

				<FMPDatePicker
					label="Delivery Date"
					path={'deliveryDate'}
					onChange={this.onChange}
					value={this.getValue('deliveryDate')}
					error={this.getError('deliveryDate')}
				/>
				<div className={classes.matchContainer}>{deliveryDateMatchSnippet}</div>
			</Fragment>
		);
	};

	renderPickupSection = () => {
		const { classes, terminals, supplierMap } = this.props;
		const { invoiceDocuments, pickupBolNumbers } = this.state;

		/** @namespace this.state.pickupNetGallons **/
		const pickupNetGallons = parseInt(this.state.pickupNetGallons, 10);
		//- Check to see if everything matches delivery

		//const {linkedBulkDeliveryId } = rackTransfer;
		//const bulkDelivery = bulkDeliveryMap[linkedBulkDeliveryId];

		const min = MIN_GALLONS_FOR_BULK_WARNING;
		const max = MAX_GALLONS_FOR_BULK_WARNING;
		let deliveryMatchSnippet =
			pickupNetGallons >= min && pickupNetGallons <= max
				? this.renderMatch(`Net gallons within expected amounts of ${min} and ${max}`)
				: this.renderMismatch([`Net gallons outside normal amounts of ${min} and ${max}`]);

		//
		//- Calc what the delivery document thinks what is saved
		//

		//- Check if carrier matches document
		let documentPickupGallons = 0;
		invoiceDocuments.forEach((invoiceDocument) => {
			const { documentType, data } = invoiceDocument;
			if (!data || documentType !== 'pickupBol') {
				return;
			}
			const { netGallons } = data;
			documentPickupGallons += parseInt(netGallons, 10);
		});

		let documentMatchSnippet = '';
		if (documentPickupGallons === pickupNetGallons) {
			documentMatchSnippet = this.renderMatch(`Net gallons match amounts in delivery documents`);
		} else {
			documentMatchSnippet = this.renderMismatch([
				`Net gallons do not match amounts in delivery documents : ${documentPickupGallons}`,
			]);
		}

		const path = 'pickupTerminalSupplier';
		const val = _.get(this.state, path, DEFAULT_TERMINAL_SUPPLIER_VALUE);

		return (
			<Fragment>
				<FMPSectionHeader label={'Fuel Pickup'} />

				<FMPTerminalSupplierSelect
					label={`Pickup Terminal`}
					supplierLabel={`Pickup Supplier`}
					terminalId={val.terminalId}
					supplierId={val.supplierId}
					path={path}
					onChange={this.onTerminalSupplierChange}
					error={this.getError(path)}
					supplierError={this.getError(path)}
					terminals={terminals}
					supplierMap={supplierMap}
					productTypes={['etoh']}
					justBulk={true}
					md={12}
				/>

				{!_.isEmpty(pickupBolNumbers) &&
					pickupBolNumbers.map((bolNumber, bolIndex) => {
						const bolCount = pickupBolNumbers.length === 1 ? '' : ` #${bolIndex + 1}`;
						return (
							<FMPText
								key={bolIndex}
								label={`BOL Number${bolCount}`}
								{...this.getInputProps(`pickupBolNumbers.${bolIndex}`)}
							/>
						);
					})}

				<FMPText label={'Net Gallons'} {...this.getInputProps(`pickupNetGallons`)} />

				<div className={classes.matchContainer}>
					{deliveryMatchSnippet}
					{documentMatchSnippet}
				</div>
				<FMPText label={'Loading Number'} {...this.getInputProps(`loadingNumber`)} />
			</Fragment>
		);
	};

	renderOffloadSection = () => {
		const { classes, terminals } = this.props;
		const { invoiceDocuments, offloadBolNumbers } = this.state;

		/** @namespace this.state.offloadNetGallons **/
		const offloadNetGallons = parseInt(this.state.offloadNetGallons, 10);
		//- Check to see if everything matches delivery

		//const {linkedBulkDeliveryId } = rackTransfer;
		//const bulkDelivery = bulkDeliveryMap[linkedBulkDeliveryId];

		const min = MIN_GALLONS_FOR_BULK_WARNING;
		const max = MAX_GALLONS_FOR_BULK_WARNING;
		let deliveryMatchSnippet =
			offloadNetGallons >= min && offloadNetGallons <= max
				? this.renderMatch(`Net gallons within expected amounts of ${min} and ${max}`)
				: this.renderMismatch([`Net gallons outside normal amounts of ${min} and ${max}`]);

		//
		//- Calc what the delivery document thinks what is saved
		//

		//- Check if carrier matches document
		let documentOffloadGallons = 0;
		let hasInvoiceDocument = false;
		invoiceDocuments.forEach((invoiceDocument) => {
			const { documentType, data } = invoiceDocument;
			if (!data || documentType !== 'offloadBol') {
				return;
			}
			hasInvoiceDocument = true;
			const { netGallons } = data;
			documentOffloadGallons += parseInt(netGallons, 10);
		});

		let documentMatchSnippet = '';
		if (hasInvoiceDocument) {
			if (documentOffloadGallons === offloadNetGallons) {
				documentMatchSnippet = this.renderMatch(`Net gallons match amounts in delivery documents`);
			} else {
				documentMatchSnippet = this.renderMismatch([
					`Net gallons do not match amounts in delivery documents : ${documentOffloadGallons}`,
				]);
			}
		} else {
			documentMatchSnippet = this.renderNotSure([`Offload BOL not provided to verify gallons.`]);
		}

		return (
			<Fragment>
				<FMPSectionHeader label={'Fuel Offload'} />

				<FMPTerminalSelect
					label={`Offload Terminal`}
					{...this.getInputProps(`offloadTerminalId`)}
					terminals={terminals}
					productTypes={['etoh']}
					noFilter={true}
				/>

				{!_.isEmpty(offloadBolNumbers) &&
					offloadBolNumbers.map((bolNumber, bolIndex) => {
						const bolCount = offloadBolNumbers.length === 1 ? '' : ` #${bolIndex + 1}`;
						return (
							<FMPText
								key={bolIndex}
								label={`BOL Number${bolCount}`}
								{...this.getInputProps(`offloadBolNumbers.${bolIndex}`)}
							/>
						);
					})}

				<FMPText label={'Net Gallons'} {...this.getInputProps(`offloadNetGallons`)} />

				<div className={classes.matchContainer}>
					{deliveryMatchSnippet}
					{documentMatchSnippet}
				</div>
			</Fragment>
		);
	};
}

SaveRackTransfer.propTypes = {
	isDwp: PropTypes.bool, //- from authUserContainer

	getRackTransfer: PropTypes.func.isRequired,
	rackTransfer: PropTypes.object,
	bulkDeliveryMap: PropTypes.object,
	bulkCustomerMap: PropTypes.object,
	gasFormulaMap: PropTypes.object,
	invoiceDocumentMap: PropTypes.object,
	resourceMap: PropTypes.object,

	updateRackTransfer: PropTypes.func.isRequired,
	invalidateRackTransfer: PropTypes.func.isRequired,

	searchBulkCustomers: PropTypes.func.isRequired,
	bulkCustomers: PropTypes.array,

	searchCarriers: PropTypes.func.isRequired,
	carriers: PropTypes.array,

	searchTerminals: PropTypes.func.isRequired,
	terminals: PropTypes.array,
	supplierMap: PropTypes.object,
	convertToPages: PropTypes.func,

	classes: PropTypes.object.isRequired,
	theme: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
	history: PropTypes.object.isRequired,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	RackTransferContainer,
	BulkCustomersContainer,
	CarriersContainer,
	TerminalsContainer,
	DocumentContainer,
	AuthUserContainer,
])(SaveRackTransfer);
